import { defaultUrlTransform } from 'react-markdown';
import { getBestPersonProfileLinkId } from '@/lib/personProfileLinkId';

/** Parse neogtm://account/{id} or neogtm://person/{id} deep links from chatbot markdown. */
export function parseNeogtmDeepLink(href) {
  if (!href || typeof href !== 'string' || !href.startsWith('neogtm://')) return null;
  try {
    const raw = decodeURIComponent(href.trim());
    const rest = raw.slice('neogtm://'.length);
    const qIndex = rest.indexOf('?');
    const pathPart = qIndex >= 0 ? rest.slice(0, qIndex) : rest;
    const queryPart = qIndex >= 0 ? rest.slice(qIndex + 1) : '';

    const slash = pathPart.indexOf('/');
    if (slash < 0) return null;
    const type = pathPart.slice(0, slash).trim();
    const id = pathPart.slice(slash + 1).trim();
    if (!type || !id) return null;
    if (type !== 'account' && type !== 'person') return null;

    let accountId = null;
    if (queryPart) {
      const params = new URLSearchParams(queryPart);
      accountId =
        params.get('account_id') ||
        params.get('accountId') ||
        params.get('account') ||
        null;
    }

    return { type, id, accountId };
  } catch {
    return null;
  }
}

export function isLikelyLinkIndex(id) {
  return /^\d{1,4}$/.test(String(id ?? '').trim());
}

export function extractLinkRegistryFromChatbotResponse(data) {
  if (!data || typeof data !== 'object') return null;
  const raw =
    data.link_registry ??
    data.linkRegistry ??
    data.linkable_entities ??
    data.linkableEntities ??
    data.entity_links ??
    data.entityLinks ??
    null;
  if (!raw) return null;
  if (Array.isArray(raw)) {
    const map = {};
    raw.forEach((entry, index) => {
      if (!entry || typeof entry !== 'object') return;
      const key = String(entry.key ?? entry.index ?? entry.id ?? index + 1);
      map[key] = entry;
    });
    return Object.keys(map).length ? map : null;
  }
  if (typeof raw === 'object') return raw;
  return null;
}

function lookupLinkRegistry(registry, key) {
  if (!registry) return null;
  const k = String(key).trim();
  if (!k) return null;
  if (registry[k]) return registry[k];
  if (Array.isArray(registry)) {
    const idx = Number(k);
    if (Number.isFinite(idx) && registry[idx] != null) return registry[idx];
    if (Number.isFinite(idx) && registry[idx - 1] != null) return registry[idx - 1];
  }
  return null;
}

function normalizeRegistryEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const type = String(
    entry.type ?? entry.target_type ?? entry.targetType ?? '',
  ).trim();
  const id = String(
    entry.target_id ??
      entry.targetId ??
      entry.id ??
      entry.person_id ??
      entry.personId ??
      entry.account_id ??
      entry.accountId ??
      '',
  ).trim();
  const accountId = String(
    entry.account_id ?? entry.accountId ?? entry.account ?? '',
  ).trim();
  if (!type || !id) return null;
  return {
    type: type === 'persona' ? 'person' : type,
    id,
    accountId: accountId || null,
  };
}

function findPersonByDisplayName(people, label) {
  const norm = String(label || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (!norm || !Array.isArray(people)) return null;

  return (
    people.find((p) => {
      const first = String(p?.first_name || '').trim();
      const last = String(p?.last_name || '').trim();
      const full = `${first} ${last}`.trim().toLowerCase();
      if (!full) return false;
      if (full === norm) return true;
      if (norm.includes(full) || full.includes(norm)) return true;
      return false;
    }) || null
  );
}

/** Resolve neogtm href to routable entity ids (handles BE link indices + registry). */
export function resolveNeogtmTarget(href, linkRegistry, options = {}) {
  const parsed = parseNeogtmDeepLink(href);
  if (!parsed) return null;

  let { type, id, accountId } = parsed;
  const registryEntry = normalizeRegistryEntry(lookupLinkRegistry(linkRegistry, id));
  if (registryEntry) {
    type = registryEntry.type;
    id = registryEntry.id;
    accountId = accountId || registryEntry.accountId;
  }

  if (type === 'person' && isLikelyLinkIndex(id) && options.people?.length) {
    const match = findPersonByDisplayName(options.people, options.linkLabel);
    if (match) {
      id = getBestPersonProfileLinkId(match) || match.person_id || match.id;
      accountId = accountId || match.account_id || null;
    }
  }

  if (!type || !id) return null;
  return { type, id, accountId };
}

/** Build internal page path (before createPageUrl) for account/person targets. */
export function buildChatbotEntityPagePath(type, id, options = {}) {
  if (id == null || String(id).trim() === '') return null;
  const encodedId = encodeURIComponent(String(id).trim());

  if (type === 'person') {
    const accountId = options.accountId;
    if (accountId != null && String(accountId).trim() !== '') {
      return `PersonProfile?id=${encodedId}&accountId=${encodeURIComponent(String(accountId).trim())}`;
    }
    return `PersonProfile?id=${encodedId}`;
  }
  if (type === 'account') {
    return `AccountFinal?id=${encodedId}`;
  }
  return null;
}

/** Persona profile deep-link from chatbot compose_via_persona chips. */
export function buildComposeViaPersonaPagePath({
  personId,
  accountId,
  composeBrief,
  autoGenerate = true,
} = {}) {
  const id = personId != null ? String(personId).trim() : '';
  if (!id) return null;

  const params = new URLSearchParams();
  params.set('id', id);

  const scopedAccountId = accountId != null ? String(accountId).trim() : '';
  if (scopedAccountId) params.set('accountId', scopedAccountId);

  const brief = composeBrief != null ? String(composeBrief).trim() : '';
  if (brief) params.set('custom_context', brief);

  if (autoGenerate) params.set('auto_generate', '1');

  return `PersonProfile?${params.toString()}`;
}

export function buildBriefTargetPageUrl(item) {
  const targetType = String(item?.chipTargetType ?? '').trim();
  const targetId = item?.chipTargetId;
  if (targetType === 'account' && targetId != null) {
    const encodedId = encodeURIComponent(String(targetId).trim());
    const subtype = String(item?.chipTargetSubtype ?? '').trim().toLowerCase();
    if (subtype === 'personas') {
      return `AccountFinal?id=${encodedId}&tab=personas`;
    }
    return `AccountFinal?id=${encodedId}`;
  }
  return buildChatbotEntityPagePath(targetType, targetId, {
    accountId: item?.accountId,
  });
}

export function buildNeogtmDeepLinkPagePath(href, options = {}) {
  const resolved = resolveNeogtmTarget(href, options.linkRegistry, options);
  if (!resolved) return null;
  if (resolved.type === 'person' && isLikelyLinkIndex(resolved.id)) return null;
  return buildChatbotEntityPagePath(resolved.type, resolved.id, {
    accountId: resolved.accountId,
  });
}

/** Allow neogtm:// through react-markdown's defaultUrlTransform (otherwise href is stripped). */
export function chatbotMarkdownUrlTransform(url) {
  if (typeof url === 'string' && url.startsWith('neogtm://')) return url;
  return defaultUrlTransform(url);
}

export function navigateToNeogtmDeepLink(href, navigate, createPageUrlFn, options = {}) {
  const pagePath = buildNeogtmDeepLinkPagePath(href, options);
  if (!pagePath || typeof navigate !== 'function') return false;
  navigate(createPageUrlFn(pagePath));
  return true;
}
